import { ExerciseBlock } from '@/types/workout';

export type ExerciseFocus = 'mobility' | 'strength' | 'core' | 'balance' | 'flexibility';
export type ExerciseIntensity = 'gentle' | 'moderate' | 'challenging';

export interface ExerciseTemplate {
  id: string;
  name: string;
  instruction: string;
  focus: ExerciseFocus[];
  intensity: ExerciseIntensity;
  defaultDuration: number; // seconds for time-based
  defaultSets?: number;
  defaultReps?: number;
  isWarmup?: boolean;
  isCooldown?: boolean;
}

/**
 * Local exercise library for client-side workout generation.
 * This is used as a fallback when the backend is not available.
 * The primary workout generation happens via the gym_agent API.
 */
export const exerciseLibrary: ExerciseTemplate[] = [
  // Warmup exercises
  {
    id: 'warmup-arm-circles',
    name: 'Arm Circles',
    instruction: 'Circle your arms forward, then backward',
    focus: ['mobility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isWarmup: true,
  },
  {
    id: 'warmup-high-knees',
    name: 'High Knees',
    instruction: 'March in place, bringing knees up high',
    focus: ['mobility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isWarmup: true,
  },
  {
    id: 'warmup-hip-circles',
    name: 'Hip Circles',
    instruction: 'Stand on one leg, circle the other hip',
    focus: ['mobility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isWarmup: true,
  },

  // Main exercises
  {
    id: 'plank',
    name: 'Plank',
    instruction: 'Hold a straight-arm or forearm plank position',
    focus: ['core', 'strength'],
    intensity: 'moderate',
    defaultDuration: 30,
  },
  {
    id: 'pushups',
    name: 'Push-ups',
    instruction: 'Standard push-ups with full range of motion',
    focus: ['strength'],
    intensity: 'moderate',
    defaultSets: 3,
    defaultReps: 10,
  },
  {
    id: 'squats',
    name: 'Bodyweight Squats',
    instruction: 'Squat down until thighs are parallel to floor',
    focus: ['strength'],
    intensity: 'moderate',
    defaultSets: 3,
    defaultReps: 12,
  },
  {
    id: 'lunges',
    name: 'Walking Lunges',
    instruction: 'Step forward into a lunge, alternating legs',
    focus: ['strength', 'balance'],
    intensity: 'moderate',
    defaultSets: 3,
    defaultReps: 10,
  },
  {
    id: 'mountain-climbers',
    name: 'Mountain Climbers',
    instruction: 'In plank position, drive knees toward chest alternately',
    focus: ['core', 'strength'],
    intensity: 'challenging',
    defaultDuration: 30,
  },
  {
    id: 'burpees',
    name: 'Burpees',
    instruction: 'Full body explosive movement: squat, plank, push-up, jump',
    focus: ['strength'],
    intensity: 'challenging',
    defaultSets: 3,
    defaultReps: 8,
  },
  {
    id: 'dead-bug',
    name: 'Dead Bug',
    instruction: 'Lying on back, extend opposite arm and leg',
    focus: ['core'],
    intensity: 'gentle',
    defaultDuration: 40,
  },
  {
    id: 'bird-dog',
    name: 'Bird Dog',
    instruction: 'On all fours, extend opposite arm and leg',
    focus: ['core', 'balance'],
    intensity: 'gentle',
    defaultDuration: 40,
  },
  {
    id: 'glute-bridge',
    name: 'Glute Bridge',
    instruction: 'Lying on back, lift hips toward ceiling',
    focus: ['strength'],
    intensity: 'gentle',
    defaultSets: 3,
    defaultReps: 15,
  },

  // Cooldown exercises
  {
    id: 'cooldown-child-pose',
    name: "Child's Pose",
    instruction: 'Kneel and fold forward, arms extended',
    focus: ['flexibility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isCooldown: true,
  },
  {
    id: 'cooldown-cat-cow',
    name: 'Cat-Cow Stretch',
    instruction: 'Alternate arching and rounding your spine',
    focus: ['flexibility', 'mobility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isCooldown: true,
  },
  {
    id: 'cooldown-hamstring-stretch',
    name: 'Seated Hamstring Stretch',
    instruction: 'Sit and reach toward your toes',
    focus: ['flexibility'],
    intensity: 'gentle',
    defaultDuration: 30,
    isCooldown: true,
  },
];

/**
 * Get an exercise by its ID
 */
export function getExerciseById(id: string): ExerciseTemplate | undefined {
  return exerciseLibrary.find(ex => ex.id === id);
}

/**
 * Get alternative exercises for swapping
 */
export function getAlternatives(
  exerciseId: string,
  _workout: { exercises: ExerciseBlock[] }
): ExerciseBlock[] {
  const exercise = getExerciseById(exerciseId);
  if (!exercise) return [];

  // Find exercises with similar focus that aren't in the workout
  const alternatives = exerciseLibrary
    .filter(ex => {
      // Skip the current exercise
      if (ex.id === exerciseId) return false;
      // Skip warmup/cooldown
      if (ex.isWarmup || ex.isCooldown) return false;
      // Match at least one focus area
      return ex.focus.some(f => exercise.focus.includes(f));
    })
    .slice(0, 3)
    .map((template, index) => ({
      id: `alt-${template.id}-${index}`,
      exercise_id: template.id,
      name: template.name,
      instruction: template.instruction,
      target: {
        mode: template.defaultSets ? 'reps' as const : 'time' as const,
        sets: template.defaultSets,
        reps: template.defaultReps,
        duration_seconds: template.defaultDuration,
      },
      rest_seconds: 15,
      order: index,
    }));

  return alternatives;
}
